import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts";
import { Button } from "../Button";
import { AuthInput } from "./AuthInput";

/**
 * ForgotPasswordForm - Password reset request form.
 * Sends a reset email via Supabase Auth.
 */
export function ForgotPasswordForm() {
  const { resetPassword, isLoading } = useAuth();

  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{ email?: string; form?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!email) {
      newErrors.email = "Please enter your email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) return;

    setIsSubmitting(true);

    const { error } = await resetPassword(email);

    setIsSubmitting(false);

    if (error) {
      let friendlyMessage = "Something went wrong. Please try again.";

      if (error.message?.includes("rate limit")) {
        friendlyMessage = "Too many requests. Please wait a moment and try again.";
      }

      setErrors({ form: friendlyMessage });
      return;
    }

    setShowSuccess(true);
  };

  // Success state after sending reset email
  if (showSuccess) {
    return (
      <div className="text-center space-y-6">
        <div className="text-6xl">✨</div>
        <h2 className="font-display font-bold text-display-sm text-magic-600">
          Check Your Email
        </h2>
        <p className="font-body text-body-lg text-neutral-600">
          If an account exists for <strong className="text-magic-600">{email}</strong>,
          we've sent instructions to reset your password.
        </p>
        <div className="pt-4 space-y-4">
          <Link to="/login">
            <Button variant="primary" fullWidth>
              Back to Login
            </Button>
          </Link>
        </div>
        <p className="font-body text-body-sm text-neutral-500">
          Didn't receive the email? Check your spam folder or{" "}
          <button
            type="button"
            onClick={() => setShowSuccess(false)}
            className="text-magic-600 hover:text-magic-700 transition-colors"
          >
            try again
          </button>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <p className="font-body text-body-md text-neutral-600 text-center">
        Enter your email address and we'll send you a link to reset your password.
      </p>

      {/* Form error message */}
      {errors.form && (
        <div
          className="
            p-4 rounded-xl
            bg-fairy-50 border-2 border-fairy-200
            text-fairy-700 font-body text-body-md
            flex items-start gap-3
          "
          role="alert"
        >
          <span className="text-xl flex-shrink-0">😕</span>
          <span>{errors.form}</span>
        </div>
      )}

      <AuthInput
        id="email"
        type="email"
        label="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="parent@example.com"
        error={errors.email}
        autoComplete="email"
        autoFocus
        disabled={isSubmitting || isLoading}
      />

      <Button
        type="submit"
        variant="primary"
        fullWidth
        size="lg"
        isLoading={isSubmitting || isLoading}
        disabled={isSubmitting || isLoading}
      >
        Send Reset Link
      </Button>

      <p className="font-body text-body-md text-neutral-600 text-center">
        Remember your password?{" "}
        <Link
          to="/login"
          className="text-magic-600 font-semibold hover:text-magic-700 transition-colors"
        >
          Log in
        </Link>
      </p>
    </form>
  );
}

export default ForgotPasswordForm;
